import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatINR, formatApiError, API_BASE, downloadVoucher, printTicket } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Tag, Loader2, ArrowLeft, MapPin, CreditCard, Printer, Download } from "lucide-react";
import { trackEvent } from "@/lib/useAnalytics";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();

  const productId = searchParams.get("productId");
  const travelDate = searchParams.get("travelDate");
  const checkoutDate = searchParams.get("checkoutDate") || "";
  const adults = Number(searchParams.get("adults") || 1);
  const children = Number(searchParams.get("children") || 0);
  const infants = Number(searchParams.get("infants") || 0);
  const vehicleType = searchParams.get("vehicleType") || "";
  const numVehicles = Number(searchParams.get("numVehicles") || 1);
  const roomCategory = searchParams.get("roomCategory") || "";
  const numRooms = Number(searchParams.get("numRooms") || 1);
  const extraAdults = Number(searchParams.get("extraAdults") || 0);
  const extraChildren = Number(searchParams.get("extraChildren") || 0);

  const [product, setProduct] = useState(null);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [contact, setContact] = useState({
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: user?.phone || "",
    pickup_location: "",
    pickup_point_id: "",
    special_requests: "",
  });
  const [agree, setAgree] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [paymentType, setPaymentType] = useState("full");
  const [step, setStep] = useState("details"); // details | confirmation
  const [bookingResult, setBookingResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loyaltyBalance, setLoyaltyBalance] = useState(0);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [loyaltyRedeem, setLoyaltyRedeem] = useState(0); // points to redeem
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0); // INR discount from points

  useEffect(() => {
    if (productId) {
      api.get(`/products/${productId}`).then((r) => {
        setProduct(r.data);
        if (r.data.advance_payment_enabled) setPaymentType("full");
      });
      api.get(`/products/${productId}/pickup-points`).then((r) => setPickupPoints(r.data)).catch(() => {});
    }
    // Fetch loyalty balance
    if (user) {
      api.get("/loyalty/balance").then((r) => setLoyaltyBalance(r.data.points || 0)).catch(() => {});
      api.get("/loyalty/config").then((r) => setLoyaltyConfig(r.data)).catch(() => {});
    }
  }, [productId, user]);

  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-muted-foreground">Loading...</div>;
  }

  // Determine booking type
  const bookingType = product.booking_type || "per_person";

  // Calculate pricing based on booking type
  let baseSub = 0;
  let paxLabel = "";
  let numNights = 1;

  if (bookingType === "per_vehicle") {
    const vo = (product.vehicle_options || []).find((v) => v.type === vehicleType);
    baseSub = vo ? vo.price * numVehicles : product.adult_price * numVehicles;
    paxLabel = `${numVehicles} × ${vehicleType || "vehicle"}`;
  } else if (bookingType === "per_room") {
    const rc = (product.room_categories || []).find((r) => r.name === roomCategory);
    if (checkoutDate && travelDate) {
      const d1 = new Date(travelDate), d2 = new Date(checkoutDate);
      numNights = Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
    }
    const roomRate = rc ? rc.price_per_night : product.adult_price;
    baseSub = roomRate * numRooms * numNights;
    baseSub += extraAdults * (rc?.extra_adult_charge || 0) * numNights;
    baseSub += extraChildren * (rc?.extra_child_charge || 0) * numNights;
    paxLabel = `${numRooms} room${numRooms > 1 ? "s" : ""} × ${numNights} night${numNights > 1 ? "s" : ""}`;
  } else {
    baseSub = adults * product.adult_price + children * product.child_price + infants * (product.infant_price || 0);
    paxLabel = `${adults}A ${children > 0 ? `${children}C ` : ""}${infants > 0 ? `${infants}I` : ""}`.trim();
  }

  const serviceCharge = baseSub * (product.service_charge || 0) / 100;
  const discount = appliedCoupon?.discount || 0;
  const subtotal = Math.max(0, baseSub + serviceCharge - discount - loyaltyDiscount);
  const gstRate = product.gst_rate != null ? product.gst_rate : 5;
  const gst = subtotal * gstRate / 100;
  const total = subtotal + gst;

  // Advance payment calculation
  const advanceEnabled = product.advance_payment_enabled;
  let advanceAmount = total;
  if (advanceEnabled && paymentType === "advance") {
    const fixed = product.advance_payment_fixed || 0;
    const pct = product.advance_payment_percent || 50;
    advanceAmount = fixed > 0 ? Math.min(fixed, total) : Math.round(total * pct / 100);
  }
  const balanceAmount = paymentType === "advance" ? total - advanceAmount : 0;
  const payableNow = paymentType === "advance" ? advanceAmount : total;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/apply", {
        code: coupon.trim().toUpperCase(),
        subtotal: baseSub,
        category: product.category,
      });
      setAppliedCoupon(data);
      toast.success(`Coupon applied! You save ${formatINR(data.discount)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid coupon");
      setAppliedCoupon(null);
    }
  };

  const finalizeBooking = (confirmed) => {
    try {
      trackEvent("purchase", {
        transaction_id: confirmed?.reference_no,
        value: Number(confirmed?.total_amount || 0),
        currency: "INR",
        coupon: appliedCoupon?.code,
        items: [{
          item_id: product.id,
          item_name: product.title,
          item_category: product.category,
          item_variant: product.sub_category || undefined,
          price: Number(confirmed?.total_amount || 0),
          quantity: (Number(confirmed?.adults) || 0) + (Number(confirmed?.children) || 0),
        }],
      });
    } catch (_e) { /* analytics never blocks UX */ }
    setBookingResult(confirmed);
    setStep("confirmation");
  };

  const handlePay = async () => {
    if (!agree) return toast.error("Please accept terms & cancellation policy");
    if (!contact.contact_name || !contact.contact_email || !contact.contact_phone) {
      return toast.error("Please fill all contact details");
    }
    setLoading(true);
    try {
      // 1. Create booking
      const { data: booking } = await api.post("/bookings", {
        product_id: product.id,
        travel_date: travelDate,
        checkout_date: checkoutDate || undefined,
        adults, children, infants, seniors: 0,
        vehicle_type: vehicleType || undefined,
        num_vehicles: numVehicles,
        room_category: roomCategory || undefined,
        num_rooms: numRooms,
        extra_adults: extraAdults,
        extra_children: extraChildren,
        coupon_code: appliedCoupon?.code,
        payment_type: paymentType,
        loyalty_points_redeemed: loyaltyRedeem > 0 ? loyaltyRedeem : undefined,
        pickup_point_id: contact.pickup_point_id || undefined,
        ...contact,
        passengers: [],
      });

      // 2. Create payment order
      const { data: order } = await api.post("/payments/create-order", { booking_id: booking.id });

      // 3. Process payment (mock or razorpay)
      if (order.mode === "mock") {
        // Mock only works in development/preview
        if (window.location.hostname !== "localhost" && !window.location.hostname.includes("preview")) {
          toast.error("Payment gateway not configured. Please contact support.");
          setLoading(false);
          return;
        }
        // Simulate Razorpay UX in dev
        await new Promise((r) => setTimeout(r, 1200));
        const { data: confirmed } = await api.post("/payments/verify", {
          booking_id: booking.id,
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `mock_pay_${Date.now()}`,
        });
        finalizeBooking(confirmed);
      } else {
        // Real Razorpay flow
        if (!window.Razorpay) {
          toast.error("Payment service failed to load. Please refresh the page and try again.");
          setLoading(false);
          return;
        }
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: settings.site_name || "HyderabadTourism.in",
          description: product.title,
          order_id: order.order_id,
          prefill: { name: contact.contact_name, email: contact.contact_email, contact: contact.contact_phone },
          theme: { color: "#006666" },
          handler: async (resp) => {
            const { data: confirmed } = await api.post("/payments/verify", {
              booking_id: booking.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            finalizeBooking(confirmed);
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rzp.open();
        return;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  if (step === "confirmation" && bookingResult) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="booking-confirmation">
        <div className="bg-white border border-border rounded-2xl p-8 sm:p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-12 h-12 text-success" />
          </div>
          <h1 className="font-display text-3xl mb-2">Booking Confirmed!</h1>
          <p className="text-muted-foreground mb-6">An e-voucher has been sent to {bookingResult.contact_email} and WhatsApp.</p>

          <div className="bg-secondary/50 rounded-xl p-6 text-left max-w-md mx-auto space-y-2">
            <div className="flex justify-between"><span className="text-muted-foreground">Reference</span><span className="font-bold text-teal" data-testid="booking-ref">{bookingResult.reference_no}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Product</span><span className="font-medium text-right">{bookingResult.product_title}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Travel Date</span><span>{bookingResult.travel_date}{bookingResult.checkout_date ? ` — ${bookingResult.checkout_date}` : ""}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Details</span><span>
              {bookingResult.booking_type === "per_vehicle" ? `${bookingResult.num_vehicles || 1} × ${bookingResult.vehicle_type || "Vehicle"}` :
               bookingResult.booking_type === "per_room" ? `${bookingResult.num_rooms || 1} Room${(bookingResult.num_rooms || 1) > 1 ? "s" : ""} × ${bookingResult.num_nights || 1} Night${(bookingResult.num_nights || 1) > 1 ? "s" : ""}` :
               `${bookingResult.adults}A ${bookingResult.children > 0 ? `${bookingResult.children}C` : ""}`}
            </span></div>
            {bookingResult.payment_type === "advance" && (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Amount</span><span>{formatINR(bookingResult.total_amount)}</span></div>
                <div className="flex justify-between text-success"><span>Advance Paid</span><span>{formatINR(bookingResult.advance_amount)}</span></div>
                <div className="flex justify-between text-saffron"><span>Balance Due</span><span>{formatINR(bookingResult.balance_amount)}</span></div>
              </>
            )}
            {bookingResult.payment_type !== "advance" && (
              <div className="flex justify-between text-lg pt-2 border-t border-border mt-2"><span className="font-semibold">Paid</span><span className="font-bold text-saffron">{formatINR(bookingResult.total_amount)}</span></div>
            )}
            {bookingResult.pickup_location && (
              <div className="flex justify-between pt-1"><span className="text-muted-foreground">Pickup</span><span className="text-right">{bookingResult.pickup_location}</span></div>
            )}
            {bookingResult.special_requests && (
              <div className="flex justify-between pt-1"><span className="text-muted-foreground">Special Requests</span><span className="text-right max-w-[240px]">{bookingResult.special_requests}</span></div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => downloadVoucher(bookingResult.id, bookingResult.reference_no)} className="bg-teal hover:bg-teal-dark text-white" data-testid="download-voucher">
              <Download className="w-4 h-4 mr-2" /> Download Voucher (PDF)
            </Button>
            <Button variant="outline" onClick={() => printTicket(bookingResult.id)} data-testid="print-ticket">
              <Printer className="w-4 h-4 mr-2" /> Print Ticket
            </Button>
            <Button variant="outline" onClick={() => navigate("/my-bookings")} data-testid="view-bookings">My Bookings</Button>
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-home">Back to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="checkout-page">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-teal mb-4 inline-flex items-center gap-1" data-testid="checkout-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-display text-3xl sm:text-4xl mb-6">Complete your booking</h1>

      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div className="space-y-6">
          {/* Contact */}
          <div className="bg-white border border-border rounded-2xl p-6">
            <h2 className="font-display text-xl mb-4">Lead Traveller Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cn">Full Name *</Label>
                <Input id="cn" value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} data-testid="checkout-name" />
              </div>
              <div>
                <Label htmlFor="ce">Email *</Label>
                <Input id="ce" type="email" value={contact.contact_email} onChange={(e) => setContact({ ...contact, contact_email: e.target.value })} data-testid="checkout-email" />
              </div>
              <div>
                <Label htmlFor="cp">Mobile *</Label>
                <Input id="cp" value={contact.contact_phone} onChange={(e) => setContact({ ...contact, contact_phone: e.target.value })} placeholder="+91 ..." data-testid="checkout-phone" />
              </div>
              <div>
                <Label htmlFor="pu">Pickup Location</Label>
                {pickupPoints.length > 0 ? (
                  <select
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    value={contact.pickup_point_id}
                    onChange={(e) => {
                      const pp = pickupPoints.find((p) => p.id === e.target.value);
                      setContact({
                        ...contact,
                        pickup_point_id: e.target.value,
                        pickup_location: pp ? `${pp.name} - ${pp.address}` : "",
                      });
                    }}
                    data-testid="checkout-pickup-select"
                  >
                    <option value="">Select a pickup point</option>
                    {pickupPoints.map((pp) => (
                      <option key={pp.id} value={pp.id}>
                        {pp.name}{pp.time ? ` (${pp.time})` : ""} — {pp.address}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input id="pu" value={contact.pickup_location} onChange={(e) => setContact({ ...contact, pickup_location: e.target.value })} placeholder="Hotel / area" data-testid="checkout-pickup" />
                )}
              </div>
            </div>
            <div className="mt-4">
              <Label htmlFor="sr">Special requests</Label>
              <Textarea id="sr" value={contact.special_requests} onChange={(e) => setContact({ ...contact, special_requests: e.target.value })} rows={2} data-testid="checkout-requests" />
            </div>
          </div>

          {/* Coupon */}
          <div className="bg-white border border-border rounded-2xl p-6">
            <h2 className="font-display text-xl mb-3 flex items-center gap-2"><Tag className="w-5 h-5 text-saffron" />Apply Coupon</h2>
            <div className="flex gap-2">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Enter coupon code" data-testid="coupon-input" />
              <Button variant="outline" onClick={applyCoupon} data-testid="apply-coupon">Apply</Button>
            </div>
            {appliedCoupon && (
              <div className="mt-3 text-sm text-success flex items-center gap-2" data-testid="coupon-applied">
                <CheckCircle2 className="w-4 h-4" />
                {appliedCoupon.code} applied — you save {formatINR(appliedCoupon.discount)}
              </div>
            )}
          </div>

          {/* Loyalty Points Redemption — only shown when admin has enabled it */}
          {loyaltyConfig?.redemption_enabled && loyaltyBalance >= (loyaltyConfig?.min_redeem || 50) && (
            <div className="bg-white border border-border rounded-2xl p-6" data-testid="loyalty-redeem-section">
              <h2 className="font-display text-xl mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-saffron" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                Redeem Loyalty Points
              </h2>
              <div className="bg-saffron/5 rounded-lg p-3 mb-3 text-sm">
                <span className="font-semibold">{loyaltyBalance.toLocaleString()} points</span> available
                <span className="text-muted-foreground ml-1">(= {formatINR(loyaltyBalance * (loyaltyConfig?.point_value_inr || 1))} value)</span>
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Points to use (min {loyaltyConfig?.min_redeem || 50})</Label>
                  <Input
                    type="number"
                    min={0}
                    max={loyaltyBalance}
                    value={loyaltyRedeem}
                    onChange={(e) => {
                      const pts = Math.min(Math.max(0, Number(e.target.value)), loyaltyBalance);
                      setLoyaltyRedeem(pts);
                      setLoyaltyDiscount(pts >= (loyaltyConfig?.min_redeem || 50) ? pts * (loyaltyConfig?.point_value_inr || 1) : 0);
                    }}
                    data-testid="loyalty-redeem-input"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setLoyaltyRedeem(loyaltyBalance);
                    setLoyaltyDiscount(loyaltyBalance * (loyaltyConfig?.point_value_inr || 1));
                  }}
                  data-testid="use-all-points"
                >
                  Use All
                </Button>
              </div>
              {loyaltyDiscount > 0 && (
                <div className="mt-2 text-sm text-success font-medium" data-testid="loyalty-discount-preview">
                  You save {formatINR(loyaltyDiscount)} with {loyaltyRedeem} points
                </div>
              )}
            </div>
          )}

          {/* Payment Type */}
          {advanceEnabled && (
            <div className="bg-white border border-border rounded-2xl p-6" data-testid="payment-type-section">
              <h2 className="font-display text-xl mb-3 flex items-center gap-2"><CreditCard className="w-5 h-5 text-teal" />Payment Option</h2>
              <RadioGroup value={paymentType} onValueChange={setPaymentType} className="space-y-3">
                <label className="flex items-start gap-3 border border-border rounded-xl p-4 cursor-pointer hover:bg-muted/30 transition-colors" data-testid="payment-type-full">
                  <RadioGroupItem value="full" />
                  <div>
                    <div className="font-medium text-sm">Pay Full Amount</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Pay {formatINR(total)} now and complete your booking</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 border border-border rounded-xl p-4 cursor-pointer hover:bg-muted/30 transition-colors" data-testid="payment-type-advance">
                  <RadioGroupItem value="advance" />
                  <div>
                    <div className="font-medium text-sm">Pay Advance Only</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Pay {formatINR(advanceAmount)} now • Balance {formatINR(total - advanceAmount)} due before travel
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}

          {/* T&C */}
          <div className="bg-white border border-border rounded-2xl p-6">
            <label className="flex gap-2 items-start cursor-pointer" data-testid="checkout-terms-wrapper">
              <Checkbox checked={agree} onCheckedChange={setAgree} className="mt-1" data-testid="checkout-terms" />
              <div className="text-sm">
                I have read and agree to the <Link to="/terms" className="text-teal underline">terms of service</Link>, <Link to="/privacy" className="text-teal underline">privacy policy</Link>, and the cancellation policy specific to this booking.
              </div>
            </label>
          </div>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="bg-white border border-border rounded-2xl p-6">
            <h2 className="font-display text-xl mb-4">Booking Summary</h2>
            <div className="flex gap-3 mb-4 pb-4 border-b border-border">
              <img src={product.featured_image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm line-clamp-2">{product.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{travelDate}{checkoutDate ? ` — ${checkoutDate}` : ""}</div>
                <div className="text-xs text-muted-foreground">{paxLabel}</div>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Base amount</span><span>{formatINR(baseSub)}</span></div>
              {serviceCharge > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Service charge</span><span>{formatINR(serviceCharge)}</span></div>}
              {discount > 0 && <div className="flex justify-between text-success"><span>Discount ({appliedCoupon.code})</span><span>- {formatINR(discount)}</span></div>}
              {loyaltyDiscount > 0 && <div className="flex justify-between text-saffron"><span>Loyalty Points ({loyaltyRedeem} pts)</span><span>- {formatINR(loyaltyDiscount)}</span></div>}
              {gst > 0 && <div className="flex justify-between"><span className="text-muted-foreground">GST ({gstRate}%)</span><span>{formatINR(gst)}</span></div>}
              <div className="border-t border-border pt-2 mt-2 flex justify-between font-bold text-lg">
                <span>Total</span><span className="text-teal" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
              {paymentType === "advance" && advanceEnabled && (
                <>
                  <div className="flex justify-between text-sm text-success font-medium">
                    <span>Pay Now (Advance)</span><span>{formatINR(advanceAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-saffron">
                    <span>Balance Due</span><span>{formatINR(balanceAmount)}</span>
                  </div>
                </>
              )}
            </div>

            <Button onClick={handlePay} disabled={loading} className="w-full mt-6 bg-saffron hover:bg-saffron-dark text-white py-6 text-base font-semibold rounded-xl" data-testid="pay-now-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay ${formatINR(payableNow)}`}
            </Button>

            <div className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5 justify-center">
              <ShieldCheck className="w-3.5 h-3.5 text-teal" /> Secure • Razorpay protected
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
